type Entries<T> = { [K in keyof T]: [K, T[K]] }[keyof T][];

/** Create a new type that adds a prefix to all values of an existing string literal union type, */
export type Prefix<Prefix extends string, T extends string> = `${Prefix}${T}`;

/**
 * The opposite of the built-in `Exclude` utility type: Constructs a type from members of `UnionType` that are assignable to `IncludedMembers`.
 *
 * @see [The `Exclude` utility type](https://www.typescriptlang.org/docs/handbook/utility-types.html#excludeuniontype-excludedmembers)
 */
export type Include<UnionType, IncludedMembers extends UnionType> = IncludedMembers;

/** The opposite of the built-in `NonNullable` utility type. */
export type Nullable<T> = T | null | undefined;

/**
 * optional properties of T must be set explicitly to an actual value.
 * Use this type if you want to remind people to touch a place when the interface changes.
 *
 * @example
 * Simple example:
 * ```
 * interface I { a: string; b?: number }
 * const t1: AllPropertiesAssigned<I> = { a: 'foo', b: 1 }; // ok
 * const t2: AllPropertiesAssigned<I> = { a: 'foo', b: undefined }; // ok
 * const t3: AllPropertiesAssigned<I> = { a: 'foo' }; // TS2741: Property b is missing
 * ```
 *
 * @example
 * Make sure every value is explicitly assigned
 * ```
 * interface I { a: string; b?: number }
 * function getI() {
 *   return {
 *     a: 'foo',
 *   } satisfies AllPropertiesAssigned<I>; // TS1360: Type … does not satisfy the expected type
 * }
 * ```
 *
 * @see https://stackoverflow.com/a/52973675
 */
export type AllPropertiesAssigned<T> = { [K in keyof Required<T>]: T[K] };

export function entries<O extends object>(object: O) {
  return Object.entries(object) as Entries<O>;
}

export function keys<O extends object>(object: O) {
  return Object.keys(object) as (keyof O)[];
}
